import { Link } from "@inertiajs/react"
import { ExternalLink } from "lucide-react"
import { useTranslation } from "react-i18next"

import { ApproveSubmissionConfirmDialog } from "@/components/submissions/approve-submission-confirm-dialog"
import { FlagSubmissionConfirmDialog } from "@/components/submissions/flag-submission-confirm-dialog"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import type { SubmissionReviewItem } from "@/types/home-triage"

/**
 * OKS-707 — Contextual inline action row for a Submissions-to-review card.
 *
 * Single visibility rule (mirror the OKS-706 candidate action row): each
 * affordance renders IFF its server-supplied action URL is non-null — no
 * disabled-with-tooltip path. Reading order (UX Spec §2): Open (ghost,
 * leftmost) · Flag (corrective) · Approve (forward-primary, rightmost).
 *
 * Approve/Flag REUSE the OKS-35 confirm dialogs verbatim
 * (`ApproveSubmissionConfirmDialog` with auto-checks + optional kudo;
 * `FlagSubmissionConfirmDialog` with the required note). Both POST via an
 * Inertia `<Form>` with `preserveScroll` to the existing endpoints, which
 * redirect referer-aware back to `/brand` so the section reloads in place
 * and the count badge decrements as the card leaves the `pending` set.
 * Open is a plain Inertia `<Link>` to the mission submissions Sheet.
 */
export interface SubmissionReviewActionsProps {
  item: SubmissionReviewItem
  className?: string
}

export function SubmissionReviewActions({
  item,
  className,
}: SubmissionReviewActionsProps) {
  const { t } = useTranslation()
  const { actions, submission, mission_id: missionId, creator_handle } = item

  return (
    <div className={cn("flex flex-wrap items-center gap-2", className)}>
      {/* Open — ghost, leftmost. Jumps to the mission submissions Sheet. */}
      <Button
        asChild
        type="button"
        variant="ghost"
        size="sm"
        data-testid="submission-review-open"
      >
        <Link
          href={actions.open_url}
          aria-label={t("submissions.home_review.actions.open_aria", {
            handle:
              creator_handle ?? t("submissions.home_review.creator_unknown"),
          })}
        >
          <ExternalLink className="size-4" aria-hidden="true" />
          {t("submissions.home_review.actions.open")}
        </Link>
      </Button>

      {/* Flag — corrective. Reuses the OKS-35 flag dialog (required note).
          `action` override is null on the brand home (dialog builds the
          mission-nested path) and the agency `/agency/submissions/:id/flag`
          URL on the agency home (OKS-708). */}
      {actions.flag_url && (
        <FlagSubmissionConfirmDialog
          submission={submission}
          missionId={missionId}
          action={actions.flag_url}
          trigger={
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-testid="submission-review-flag"
            >
              {t("submissions.home_review.actions.flag")}
            </Button>
          }
        />
      )}

      {/* Approve — forward-primary, rightmost. Reuses the OKS-35 approve
          dialog (auto-checks + optional kudo). */}
      {actions.approve_url && (
        <ApproveSubmissionConfirmDialog
          submission={submission}
          missionId={missionId}
          creatorHandle={creator_handle}
          canSendKudo={item.can_send_kudo}
          action={actions.approve_url}
          trigger={
            <Button
              type="button"
              size="sm"
              data-testid="submission-review-approve"
            >
              {t("submissions.home_review.actions.approve")}
            </Button>
          }
        />
      )}
    </div>
  )
}
